/**
 * In-memory mirror of the offline state (packs, stored items, manifest, recent LRU) and the derived
 * snapshot the UI renders (useSyncExternalStore). Mutated only by manager.ts / downloader.ts.
 *
 * `generation` changes on every reset (logout, user switch): async work started for an earlier
 * generation must not write anything afterwards (see isCurrent()).
 */
import type { SheetTier } from '../../shared/dto/library';
import type { ManifestDTO } from '../../shared/dto/offline';
import { entryBytes } from './lru';
import { packSongIds, planPack, type PlanUser } from './plan';
import type { ItemRow, PackKind, PackRow, PackStatus, PlannedItem, RecentEntry } from './types';

export type SongOfflineState = 'saved' | 'downloading' | 'update' | 'pending' | 'error';

export interface PackView {
  row: PackRow;
  /** Live status: 'downloading' while the queue works on this pack. */
  status: PackStatus | 'downloading';
  songIds: string[];
  filesTotal: number;
  filesDone: number;
  bytesTotal: number;
  bytesDone: number;
}

export interface DownloadProgress {
  packId: string;
  packKind: PackKind;
  packTitle: string;
  filesDone: number;
  filesTotal: number;
  bytesDone: number;
  bytesTotal: number;
  /** Paused: waiting for the network, or waiting before a retry. */
  waiting: 'network' | 'retry' | null;
}

export interface OfflineSnapshot {
  loaded: boolean;
  userId: string | null;
  manifestAt: number | null;
  syncing: boolean;
  /** The last manifest fetch failed (offline or server error); cached data is still used. */
  syncFailed: boolean;
  packs: PackView[];
  songs: ReadonlyMap<string, SongOfflineState>;
  progress: DownloadProgress | null;
  recent: { songs: number; bytes: number };
}

export interface OfflineData {
  generation: number;
  user: (PlanUser & { id: string }) | null;
  tier: SheetTier;
  manifest: ManifestDTO | null;
  packs: Map<string, PackRow>;
  items: Map<string, ItemRow>;
  recent: RecentEntry[];
  loaded: boolean;
  syncing: boolean;
  syncFailed: boolean;
  /** Pack the downloader is working on, and progress of the file in flight. */
  activePackId: string | null;
  inflightBytes: number;
  waiting: DownloadProgress['waiting'];
}

export const data: OfflineData = {
  generation: 1,
  user: null,
  tier: 'l',
  manifest: null,
  packs: new Map(),
  items: new Map(),
  recent: [],
  loaded: false,
  syncing: false,
  syncFailed: false,
  activePackId: null,
  inflightBytes: 0,
  waiting: null,
};

export function resetData(): void {
  data.generation++;
  data.user = null;
  data.manifest = null;
  data.packs = new Map();
  data.items = new Map();
  data.recent = [];
  data.loaded = false;
  data.syncing = false;
  data.syncFailed = false;
  data.activePackId = null;
  data.inflightBytes = 0;
  data.waiting = null;
  publish();
}

export function isCurrent(gen: number): boolean {
  return gen === data.generation;
}

/* ─── Plans (cached per manifest/user/tier/pack set) ────────────────────── */

let planCache: { key: unknown[]; plans: Map<string, PlannedItem[] | null> } | null = null;

/** planPack() for every pack with the current manifest (memoised until any input changes). */
export function currentPlans(): Map<string, PlannedItem[] | null> {
  const key = [data.manifest, data.user?.role, data.user?.section, data.tier, ...[...data.packs.values()].map((p) => `${p.id}:${p.allSections}`)];
  if (planCache && planCache.key.length === key.length && planCache.key.every((v, i) => v === key[i])) return planCache.plans;
  const plans = new Map<string, PlannedItem[] | null>();
  for (const p of data.packs.values()) {
    plans.set(p.id, data.manifest && data.user ? planPack(p, data.manifest, data.user, data.tier) : null);
  }
  planCache = { key, plans };
  return plans;
}

/** Items of a pack that are not stored yet (empty when the plan is unknown). */
export function missingItems(packId: string): PlannedItem[] {
  const plan = currentPlans().get(packId);
  if (!plan) return [];
  return plan.filter((it) => {
    const stored = data.items.get(it.url);
    return !stored || stored.etag !== it.etag || stored.size !== it.size;
  });
}

/* ─── Snapshot ──────────────────────────────────────────────────────────── */

const RANK: Record<SongOfflineState, number> = { saved: 5, downloading: 4, update: 3, pending: 2, error: 1 };

function computeSnapshot(): OfflineSnapshot {
  const plans = currentPlans();
  const packs: PackView[] = [];
  const songs = new Map<string, SongOfflineState>();
  let progress: DownloadProgress | null = null;

  const sorted = [...data.packs.values()].sort((a, b) => a.createdAt - b.createdAt);
  for (const row of sorted) {
    const plan = plans.get(row.id) ?? [];
    const active = data.activePackId === row.id;
    const status: PackView['status'] = active ? 'downloading' : row.status;
    let filesDone = 0;
    let bytesDone = 0;
    let bytesTotal = 0;
    const perSong = new Map<string, boolean>(); // songId → all stored
    for (const it of plan) {
      bytesTotal += it.size;
      const s = data.items.get(it.url);
      const ok = !!s && s.etag === it.etag && s.size === it.size;
      if (ok) {
        filesDone++;
        bytesDone += it.size;
      }
      perSong.set(it.songId, (perSong.get(it.songId) ?? true) && ok);
    }
    // Songs of the pack without any downloadable file (lyrics only) are saved as soon as the pack exists.
    const songIds = data.manifest ? (packSongIds(row, data.manifest) ?? []) : [...perSong.keys()];
    for (const id of songIds) if (!perSong.has(id)) perSong.set(id, true);
    for (const [songId, done] of perSong) {
      const st: SongOfflineState = done ? 'saved' : active ? 'downloading' : row.status === 'update' ? 'update' : row.status === 'error' ? 'error' : 'pending';
      const prev = songs.get(songId);
      if (!prev || RANK[st] > RANK[prev]) songs.set(songId, st);
    }
    const view: PackView = { row, status, songIds, filesTotal: plan.length, filesDone, bytesTotal, bytesDone };
    packs.push(view);
    if (active) {
      progress = {
        packId: row.id,
        packKind: row.kind,
        packTitle: row.title,
        filesDone,
        filesTotal: plan.length,
        bytesDone: Math.min(bytesTotal, bytesDone + data.inflightBytes),
        bytesTotal,
        waiting: data.waiting,
      };
    }
  }

  let recentBytes = 0;
  for (const e of data.recent) recentBytes += entryBytes(e);

  return {
    loaded: data.loaded,
    userId: data.user?.id ?? null,
    manifestAt: data.manifest?.generatedAt ?? null,
    syncing: data.syncing,
    syncFailed: data.syncFailed,
    packs,
    songs,
    progress,
    recent: { songs: data.recent.length, bytes: recentBytes },
  };
}

let snapshot: OfflineSnapshot = computeSnapshot();
const listeners = new Set<() => void>();

/** Recomputes the snapshot and notifies subscribers. */
export function publish(): void {
  snapshot = computeSnapshot();
  for (const fn of listeners) fn();
}

let throttled: ReturnType<typeof setTimeout> | null = null;
/** publish() at most every 250 ms (download progress). */
export function publishSoon(): void {
  if (throttled) return;
  throttled = setTimeout(() => {
    throttled = null;
    publish();
  }, 250);
}

export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getSnapshot(): OfflineSnapshot {
  return snapshot;
}
